import { text, cx } from '../../styles/theme';
import { formatNumber } from '../../utils/format';
import type { AgentKeys, DashboardResponse, MetricWithDelta, TimePeriod } from '../../types/api';

const PERIOD_LABEL: Record<string, string> = {
  today: 'vs. yesterday',
  '7days': 'vs. prior 7 days',
  '30days': 'vs. prior 30 days',
};

function Delta({ pct }: { pct?: number }) {
  if (pct === undefined || !isFinite(pct)) return null;
  // Color and sign track the DISPLAYED (rounded) value, not the raw delta: a
  // sub-0.5% move rounds to 0% and must not render as a colored "+0%" — a green
  // "+0%" implies growth that the shown number contradicts (same rule as
  // Reports' KeyDelta). Treat a rounds-to-zero move as no change and omit it.
  const rounded = Math.round(pct);
  if (rounded === 0) return null;
  return (
    <span
      className={cx(
        'text-xs font-medium tabular-nums',
        rounded > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400',
      )}
    >
      {rounded > 0 ? '+' : ''}
      {rounded}%
    </span>
  );
}

function Item({ value, label, deltaPct }: { value: string; label: string; deltaPct?: number }) {
  return (
    <span className="flex items-baseline gap-1.5">
      <span className={cx('font-mono text-base font-semibold tabular-nums', text.primary)}>{value}</span>
      <span className={cx('text-sm', text.secondary)}>{label}</span>
      <Delta pct={deltaPct} />
    </span>
  );
}

function pctOf(m?: MetricWithDelta): number | undefined {
  return m?.deltaPercent;
}

/**
 * VolumeStrip is the borderless raw-volume row under the hero cards:
 * sessions, turns, files, tools-per-prompt, billable tokens — with deltas
 * against the prior period. Volume is context here, not the headline (the
 * hero cards carry the value story).
 */
export function VolumeStrip({
  dashboard,
  keys,
  period,
  loading,
}: {
  dashboard?: DashboardResponse;
  keys?: AgentKeys;
  period: TimePeriod;
  loading: boolean;
}) {
  if (loading && !dashboard) {
    return <div className="h-6 animate-pulse rounded bg-gray-100 dark:bg-navy-800" />;
  }
  if (!dashboard) return null;

  const lev = keys?.leverage;
  const levDelta =
    lev?.current !== undefined && lev.current !== -1 && lev.previous !== undefined && lev.previous !== -1 && lev.previous !== 0
      ? ((lev.current - lev.previous) / lev.previous) * 100
      : undefined;

  return (
    <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2 px-1">
      <span className={cx('text-xs font-medium uppercase tracking-wider', text.muted)}>Volume</span>
      <Item value={formatNumber(dashboard.sessions?.value ?? 0)} label="sessions" deltaPct={pctOf(dashboard.sessions)} />
      <Item value={formatNumber(dashboard.turns?.value ?? 0)} label="turns" deltaPct={pctOf(dashboard.turns)} />
      <Item value={formatNumber(dashboard.filesModified?.value ?? 0)} label="files touched" deltaPct={pctOf(dashboard.filesModified)} />
      {lev?.current !== undefined && lev.current !== -1 && (
        <Item value={lev.current.toFixed(1)} label="tools per prompt" deltaPct={levDelta} />
      )}
      <Item value={formatNumber(dashboard.costTokens?.value ?? 0)} label="billable tokens" deltaPct={pctOf(dashboard.costTokens)} />
      {PERIOD_LABEL[period] && <span className={cx('text-xs', text.muted)}>{PERIOD_LABEL[period]}</span>}
    </div>
  );
}
